import { Component, Input, OnInit } from '@angular/core';

import { DsButtonVariants, DsColors, DsPageMessageVariants, Icons, IconScale, IconStyles } from '@levelaccess/design-system';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { IRiskMetricsChartConfig, IRiskMetricsChartData } from '../../interfaces/risk-metrics-chart.interface';
import { DashboardDataFrequency } from '../../../../shared/interfaces/ws-dashboard.interface';
import { $riskMetricsChartConfig } from '../../constants/risk-metrics-chart';
import {
  ITableDsPageMessageConfig,
  ITableEmptyState,
  TableEmptyStateType,
} from '../table/ngb-table/utilities/ngb-table.interface';
import { TranslateService } from '../../translate/translate.service';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../shared/interfaces/feature-flag.interface';

@Component({
  selector: 'app-websites-apps-by-risk-management-status',
  templateUrl: './websites-apps-by-risk-management-status.component.html',
  standalone: false,
})
export class WebsitesAppsByRiskManagementStatusComponent implements OnInit {
  @Input()
  public set chartData(data: IRiskMetricsChartData[]) {
    this.chartData$.next(data);
  }

  @Input()
  public set frequency(freq: DashboardDataFrequency) {
    this.frequency$.next(freq);
  }

  @Input()
  public showHeader: boolean;

  public Icons: typeof Icons = Icons;
  public DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public chartConfig$: Observable<IRiskMetricsChartConfig>;
  public emptyStateConfig$: Observable<ITableEmptyState>;

  private chartData$: BehaviorSubject<IRiskMetricsChartData[]> = new BehaviorSubject<IRiskMetricsChartData[]>(null);
  private frequency$: BehaviorSubject<DashboardDataFrequency> = new BehaviorSubject<DashboardDataFrequency>(null);

  constructor(
    private translateService: TranslateService,
    private featureFlagService: FeatureFlagService,
  ) {}

  public ngOnInit(): void {
    this.chartConfig$ = combineLatest([this.chartData$, this.frequency$]).pipe(
      map(([data, frequency]: [IRiskMetricsChartData[], DashboardDataFrequency]) => {
        return {
          [$riskMetricsChartConfig.data]: data,
          [$riskMetricsChartConfig.frequency]: frequency ?? DashboardDataFrequency.weekly,
        };
      }),
    );

    this.emptyStateConfig$ = combineLatest([
      this.frequency$,
      this.featureFlagService
        .variation$(FeatureFlagCollection.dsPageMessage, false)
        .pipe(map(Boolean), startWith(Boolean(this.featureFlagService.variation(FeatureFlagCollection.dsPageMessage, false)))),
    ]).pipe(
      map(([frequency, useDsPageMessage]: [DashboardDataFrequency, boolean]) =>
        this.getEmptyStateConfig(frequency, useDsPageMessage),
      ),
    );
  }

  private getEmptyStateConfig(frequency: DashboardDataFrequency, useDsPageMessage: boolean): ITableEmptyState {
    const title: string = this.translateService.instant('websites_apps_by_risk_management_status_empty_state_title');
    const subtitle: string = this.getEmptyStateTranslationDescription(frequency);

    if (!useDsPageMessage) {
      return {
        iconId: 'empty-box',
        title,
        subtitle,
      };
    }

    const dsPageMessage: ITableDsPageMessageConfig = {
      icon: Icons.NotFoundBrand,
      iconColor: DsColors.Primary,
      iconScale: IconScale.FA_4X,
      iconStyle: IconStyles.Light,
      variant: DsPageMessageVariants.info,
    };

    return {
      type: TableEmptyStateType.dsPageMessage,
      title,
      subtitle,
      dsPageMessage,
    };
  }

  private getEmptyStateTranslationDescription(frequency: DashboardDataFrequency): string {
    const key: string =
      frequency === DashboardDataFrequency.weekly
        ? 'websites_apps_by_risk_management_status_empty_state_subtitle'
        : 'websites_apps_by_risk_management_status_empty_state_frequency_subtitle';
    return this.translateService.instant(key);
  }
}
